import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title);

// Wrapper now flexes to parent card's size
const Wrapper = styled.div`
  width: 100%;
  height: 100%;          /* fill the card if it sets a height */
  padding: 16px 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f9f9f9;
`;

// Controls row
const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Chart area flexes; min-height:0 is key in flex layouts
const ChartArea = styled.div`
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;
  cursor: pointer;
  color: #666;
  &:hover { color: #000; }
  svg { width: 20px; height: 20px; }
`;

const StrukturaSprzedazyChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;
    const years = [...new Set(
      data
        .map((row) => row.DataWystawienia)
        .filter(Boolean)
        .map((d) => new Date(d).getFullYear())
    )].sort((a, b) => a - b);

    setAvailableYears(years);
    setSelectedYear(years[years.length - 1]);
  }, [data]);

  useEffect(() => {
    if (!data || data.length === 0 || !selectedYear) return;

    const months = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      export: 0,
      eu: 0,
      domestic: 0,
    }));

    data.forEach((row) => {
      const date = new Date(row.DataWystawienia);
      if (isNaN(date) || date.getFullYear() !== selectedYear) return;

      const m = date.getMonth();
      const fields = ["K_15", "K_19", "K_10", "K_21", "K_17", "K_11", "K_22"];
      const value = Number(fields.map(k => row[k]).find(v => v != null && v !== "") || 0) || 0;
      if (value <= 0) return;

      if (row.K_22) months[m].export += value;
      else if (row.K_21 || row.K_11) months[m].eu += value;
      else if (row.K_15 || row.K_17 || row.K_19 || row.K_10) months[m].domestic += value;
    });

    const labels = ["Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
      "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"];

    const dom = months.map(m => m.domestic);

    const GREEN = "#2e7d32";
    const EU_BG = "rgba(158,158,158,0.45)";
    const EU_BORDER = "rgba(158,158,158,0.9)";
    const EXP_BG = "rgba(189,189,189,0.45)";
    const EXP_BORDER = "rgba(189,189,189,0.9)";

    setChartData({
      labels,
      datasets: [
        { label: "Eksport/Import", data: months.map(m => m.export), backgroundColor: EXP_BG, borderColor: EXP_BORDER, borderWidth: 1 },
        { label: "EU", data: months.map(m => m.eu), backgroundColor: EU_BG, borderColor: EU_BORDER, borderWidth: 1 },
        { label: "Krajowe", data: dom, backgroundColor: "rgba(46,125,50,0.70)", borderColor: GREEN, borderWidth: 1 },
      ],
    });
  }, [data, selectedYear]);

  if (!chartData) return <p>Ładowanie wykresu...</p>;

  return (
    <Wrapper>
      <InfoIconWrapper data-tooltip-id="structure-tooltip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="structure-tooltip"
        place="left"
        content={
          <>
            <strong>Miesięczna struktura sprzedaży</strong><br />
            Eksport/Import, EU i Krajowe – porównanie wartości wg miesięcy.
          </>
        }
      />

      <Controls>
        <label>
          Rok:&nbsp;
          <select
            value={selectedYear ?? ""}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </Controls>

      <ChartArea>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false, // fill the container
            plugins: {
              legend: { position: "top" },
              title: { display: true, text: `Miesięczna struktura sprzedaży - ${selectedYear}` },
              tooltip: {
                callbacks: {
                  label: (ctx) =>
                    `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString("pl-PL", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`,
                },
              },
            },
            scales: {
              x: {
                ticks: { maxRotation: 30, autoSkip: true },
              },
              y: {
                beginAtZero: true,
                title: { display: true, text: "Wartość sprzedaży" },
                ticks: {
                  callback: (v) =>
                    Number(v).toLocaleString("pl-PL", { maximumFractionDigits: 0 }),
                },
              },
            },
          }}
        />
      </ChartArea>
    </Wrapper>
  );
};

export default StrukturaSprzedazyChart;
